import React, { useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  FormGroup,
  Checkbox,
  Chip,
  Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import WorkflowSelector from './WorkflowSelector';
import { ADMIN_SCOPE, EDITOR_SCOPE, MAX_API_KEYS, styles, USER_SCOPE } from './ApiKeyUtil';

const ApiKeyCreateForm = ({
  newApiKeyName,
  onNameChange,
  onCreateKey,
  isLoading,
  apiKeysCount,
  selectedScopes,
  onScopeChange,
  isAdmin,
  scopeError,
  selectedWorkflows,
  onWorkflowChange,
  workflowError,
  currentUser,
  allWorkflows,
  isFetchingAllWorkflows,
}) => {
  const isAtLimit = apiKeysCount >= MAX_API_KEYS;
  const remainingKeys = MAX_API_KEYS - apiKeysCount;
  const showLimitWarning = apiKeysCount >= MAX_API_KEYS - 5;

  const getScopeSelectionDescription = () => {
    const userName = currentUser?.displayName || currentUser?.userName || 'current user';
    const userAction = `submit forms and respond to tasks as ${userName}`;

    const hasAdmin = selectedScopes.includes(ADMIN_SCOPE);
    const hasEditor = selectedScopes.includes(EDITOR_SCOPE);
    const hasUser = selectedScopes.includes(USER_SCOPE);

    const workflowCount = selectedWorkflows.length;
    const workflowPlural = workflowCount === 1 ? '' : 's';

    if (hasAdmin && hasUser) {
      return `Full admin access to all workflows, plus can ${userAction}`;
    }

    if (hasAdmin) {
      return 'Full access to all workflows and admin functions';
    }

    if (hasEditor && hasUser) {
      return `Can edit ${workflowCount} selected workflow${workflowPlural} and ${userAction}`;
    }

    if (hasEditor) {
      return `Access to ${workflowCount} selected workflow${workflowPlural} for viewing, running, and editing`;
    }

    if (hasUser) {
      return `Can ${userAction}`;
    }

    return '';
  };
  return (
    <Box sx={styles.form.container}>
      <Typography variant="subtitle2" sx={styles.form.title}>
        Create new API key
      </Typography>
      <Box sx={styles.form.inputContainer}>
        <TextField
          placeholder="Enter key name"
          value={newApiKeyName}
          onChange={e => onNameChange(e.target.value)}
          size="small"
          fullWidth
          variant="outlined"
          sx={styles.form.textField}
          disabled={isLoading || isAtLimit}
        />
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={onCreateKey}
          disabled={isLoading || isAtLimit || selectedScopes.length === 0}
          startIcon={<AddIcon />}
          sx={styles.form.button}
        >
          Create Key
          {isLoading && <CircularProgress size={16} sx={styles.form.loadingIcon} />}
        </Button>
      </Box>

      <Grid container spacing={2} className="api-scope-section">
        <Grid item xs={12}>
          <Typography variant="subtitle2" className="api-scope-title">
            Scopes <span className="api-scope-required">*</span>
          </Typography>
          {selectedScopes.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {getScopeSelectionDescription()}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12}>
          <FormGroup>
            <Grid
              container
              spacing={1}
              alignItems="flex-start"
              className={`api-scope-option ${
                selectedScopes.includes(USER_SCOPE) ? 'selected' : ''
              }`}
            >
              <Grid item sx={{ alignSelf: 'flex-start' }}>
                <Checkbox
                  checked={selectedScopes.includes(USER_SCOPE)}
                  onChange={e => onScopeChange(USER_SCOPE, e.target.checked)}
                  disabled={isLoading || isAtLimit}
                  className="api-scope-checkbox"
                />
              </Grid>
              <Grid item xs>
                <Grid container direction="column" className="api-scope-content">
                  <Typography variant="subtitle2" className="api-scope-name">
                    End user
                  </Typography>
                  <Typography variant="caption" className="api-scope-description">
                    Can submit forms, and respond to tasks under your user account
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid
              container
              spacing={1}
              className={`api-scope-option ${
                selectedScopes.includes(EDITOR_SCOPE) ? 'selected' : ''
              }`}
            >
              <Grid item sx={{ alignSelf: 'flex-start' }}>
                <Checkbox
                  checked={selectedScopes.includes(EDITOR_SCOPE)}
                  onChange={e => onScopeChange(EDITOR_SCOPE, e.target.checked)}
                  disabled={isLoading || isAtLimit}
                  className="api-scope-checkbox"
                />
              </Grid>
              <Grid item xs>
                <Grid container direction="column" className="api-scope-content">
                  <Typography variant="subtitle2" className="api-scope-name">
                    Editor
                  </Typography>
                  <Typography variant="caption" className="api-scope-description">
                    Can view, run, and edit specific selected workflows
                  </Typography>

                  {selectedScopes.includes(EDITOR_SCOPE) && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="caption"
                        sx={{ mb: 1, display: 'block', fontWeight: 500 }}
                      >
                        Select Workflows:
                      </Typography>
                      <WorkflowSelector
                        selectedWorkflows={selectedWorkflows}
                        onWorkflowChange={onWorkflowChange}
                        disabled={isLoading || isAtLimit}
                        error={workflowError}
                        allWorkflows={allWorkflows}
                        loading={isFetchingAllWorkflows}
                      />
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Grid>

            <Grid
              container
              spacing={1}
              alignItems="flex-start"
              className={`api-scope-option ${isAdmin ? '' : 'disabled'} ${
                selectedScopes.includes(ADMIN_SCOPE) ? 'selected' : ''
              }`}
            >
              <Grid item sx={{ alignSelf: 'flex-start' }}>
                <Checkbox
                  checked={selectedScopes.includes(ADMIN_SCOPE)}
                  onChange={e => onScopeChange(ADMIN_SCOPE, e.target.checked)}
                  disabled={isLoading || isAtLimit || !isAdmin}
                  className="api-scope-checkbox"
                />
              </Grid>
              <Grid item xs>
                <Grid container direction="column" className="api-scope-content">
                  <Grid container alignItems="center" spacing={1} className="api-scope-header">
                    <Grid item>
                      <Typography variant="subtitle2" className="api-scope-name">
                        Admin
                      </Typography>
                    </Grid>
                    {!isAdmin && (
                      <Grid item>
                        <Chip
                          label="Admin Only"
                          size="small"
                          color="warning"
                          variant="outlined"
                          className="api-admin-only-chip"
                        />
                      </Grid>
                    )}
                  </Grid>
                  <Typography variant="caption" className="api-scope-description">
                    Full access to all workflows, and can view, run, edit workflows
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </FormGroup>
        </Grid>
        {scopeError && (
          <Grid item xs={12}>
            <Typography variant="caption" color="error" className="api-scope-error">
              Please select at least one scope
            </Typography>
          </Grid>
        )}
      </Grid>

      {showLimitWarning && (
        <Typography
          variant="caption"
          color={isAtLimit ? 'error' : 'text.secondary'}
          sx={styles.form.warningText}
        >
          {isAtLimit
            ? `You have reached the maximum of ${MAX_API_KEYS} API keys.`
            : `You can create ${remainingKeys} more API key${remainingKeys === 1 ? '' : 's'}.`}
        </Typography>
      )}
    </Box>
  );
};

export default ApiKeyCreateForm;
